import React from "react";
import { getHorizontalDragDirection, getVerticalDragDirection } from "../helpers/getDragDirection";

type DragDirection = {
  vertical: "top" | "bottom";
  horizontal: "left" | "right";
} | null;

interface IUseDrag<ElementType = HTMLDivElement> {
  dragRef: React.RefObject<ElementType>;
  doOnDragStart?: (e: React.DragEvent<ElementType>) => void;
  doOnDragOver?: (e: React.DragEvent<ElementType>) => void;
  doOnDragEnd?: (e: React.DragEvent<ElementType>) => void;
  doOnDragLeave?: (e: React.DragEvent<ElementType>) => void;
  doOnDrop?: (e: React.DragEvent<ElementType>) => void;
}

interface IUseDragReturn<ElementType = HTMLDivElement> {
  dragDirection: DragDirection;
  dragProps: {
    draggable: boolean;
    onDragStart?: (e: React.DragEvent<ElementType>) => void;
    onDragOver?: (e: React.DragEvent<ElementType>) => void;
    onDragEnd?: (e: React.DragEvent<ElementType>) => void;
    onDragLeave?: (e: React.DragEvent<ElementType>) => void;
    onDrop?: (e: React.DragEvent<ElementType>) => void;
  };
}

export default function useDrag<ElementType extends HTMLDivElement>({
  dragRef,
  doOnDragStart,
  doOnDragOver,
  doOnDragEnd,
  doOnDragLeave,
  doOnDrop,
}: IUseDrag<ElementType>): IUseDragReturn<ElementType> {
  const [dragDirection, setDragDirection] = React.useState<DragDirection>(null);

  const onDragStart = (e: React.DragEvent<ElementType>) => {
    if (doOnDragStart) doOnDragStart(e);
  };
  const onDragOver = (e: React.DragEvent<ElementType>) => {
    if (e) e.preventDefault();
    if (doOnDragOver) doOnDragOver(e);

    const h = getHorizontalDragDirection(e, dragRef);
    const v = getVerticalDragDirection(e, dragRef);

    if (h !== dragDirection?.horizontal || v !== dragDirection?.vertical)
      setDragDirection({ horizontal: h, vertical: v });
  };
  const onDragEnd = (e: React.DragEvent<ElementType>) => {
    if (e) e.preventDefault();
    if (doOnDragEnd) doOnDragEnd(e);
    setDragDirection(null);
  };
  const onDragLeave = (e: React.DragEvent<ElementType>) => {
    if (e) e.preventDefault();
    if (doOnDragLeave) doOnDragLeave(e);
    setDragDirection(null);
  };
  const onDrop = (e: React.DragEvent<ElementType>) => {
    if (e) e.preventDefault();
    if (doOnDrop) doOnDrop(e);
    setDragDirection(null);
  };

  return {
    dragDirection,
    dragProps: { draggable: true, onDragStart, onDragOver, onDragEnd, onDragLeave, onDrop },
  };
}
